import { Google } from '@mui/icons-material';
import {
    Box,
    Button,
    FormControl,
    FormGroup,
    FormLabel,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import { calendarApiV3 as api } from 'api';
import OCalendar from 'app/ocalendar/OCalendar';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ActionButton from 'common/components/ActionButton';
import CustomSunEditor from 'common/components/CustomSunEditor';
import CustomTextValidator from 'common/components/CustomTextValidators';
import FrontImage from 'common/components/FrontImage';
import ProfileImage from 'common/components/ProfileImage';
import useStylesForm from 'common/components/styles/useStylesForm.styles';
import { useAuthLicenseAddOns } from 'common/features/licenseFeatures/useAuthLicenseAddOns';
import useFlexBoxStyles from 'common/flexBox';
import { useAbortController } from 'common/hooks';
import { useSettingValue } from 'common/hooks/useSettingValue';
import { handleUploadFileSignedUrl } from 'common/utils/file';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { useDispatch, useSelector } from 'react-redux';
import { getUser, setUserAction } from 'redux/slices/authSlice';
import {
    fetchCalendarIntegrationAction,
    fetchHasCredentialAction,
    getCredentialLevel,
    getHasCredential,
    getIsConnected,
    getPendingHasCredential,
    getProviderType,
    setHasCredential,
} from 'redux/slices/icalendarProviderSlice';
import { getOrganization } from 'redux/slices/organizationSlice';
import { getMyAccountSettingsDirty, setMyAccountSettingsDirty } from 'redux/slices/profileSlice';
import 'suneditor/dist/css/suneditor.min.css';
import { useStylesMyAccount } from './styles/useStylesMyAccount';

const MyAccount = () => {
    const { t } = useTranslation();
    const { classes: flexBox } = useFlexBoxStyles();
    const { classes: classes1 } = useStylesForm();
    const { classes: classes2, cx } = useStylesMyAccount();
    const classes = { ...classes1, ...classes2 };
    const dispatch = useDispatch();
    const user = useSelector(getUser);
    const isFormDirty = useSelector(getMyAccountSettingsDirty);
    const { enqueueSnackbar } = useSnackbar();
    const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
    const [about, setAbout] = useState(user?.about);
    const newCalendar = useSettingValue(SettingsKey.NEW_CALENDAR) ?? false;
    const getSignal = useAbortController();
    const isAuthorize = useAuthLicenseAddOns();
    const [sunError, setSunError] = useState(false);

    const [values, setValues] = useState({
        avatar: user?.avatar,
        submited: false,
        isUpdating: false,
        firstName: user?.firstName,
        lastName: user?.lastName,
        username: user?.username,
        headline: user?.headline,
        frontPage: user?.frontPage,
    });

    const organization = useSelector(getOrganization);
    const { id: organizationId } = organization;
    const isCalendarConnected = useSelector(getIsConnected);
    const integrationProviderType = useSelector(getProviderType);
    const integrationCredentialLevel = useSelector(getCredentialLevel);
    const hasCredential = useSelector(getHasCredential);
    const pendingHasCredential = useSelector(getPendingHasCredential);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const calendarVersion = useSettingValue(SettingsKey.CALENDAR_VERSION) ?? 3;

    const readOnly = false;

    const editorConfig = {
        buttonList: [
            readOnly
                ? ['fullScreen', 'codeView', 'preview']
                : [
                      'undo',
                      'redo',
                      'font',
                      'fontSize',
                      'formatBlock',
                      'paragraphStyle',
                      'blockquote',
                      'bold',
                      'underline',
                      'italic',
                      'strike',
                      'subscript',
                      'superscript',
                      'fontColor',
                      'hiliteColor',
                      'textStyle',
                      'removeFormat',
                      'outdent',
                      'indent',
                      'align',
                      'horizontalRule',
                      'list',
                      'lineHeight',
                      'link',
                      'fullScreen',
                      'showBlocks',
                      'codeView',
                      'preview',
                  ],
        ],
        height: 300,
    };

    const setAvatar = (imageValue) => {
        setValues({
            ...values,
            avatar: imageValue,
        });
        dispatch(setMyAccountSettingsDirty(true));
    };

    const setFrontPage = (imageValue) => {
        setValues({
            ...values,
            frontPage: imageValue,
        });
        dispatch(setMyAccountSettingsDirty(true));
    };

    useEffect(() => {
        setValues((currentValues) => ({
            ...currentValues,
            avatar: user?.avatar,
            firstName: user?.firstName,
            lastName: user?.lastName,
            username: user?.username,
            headline: user?.headline,
            frontPage: user?.frontPage,
        }));
        setAbout(user?.about);
    }, [user]);

    const getDto = () => {
        return {
            ...user,
            name: values.firstName + ' ' + values.lastName,
            firstName: values.firstName,
            lastName: values.lastName,
            username: values?.username,
            about: about,
            headline: values?.headline,
            avatar: values.avatar,
            frontPage: values.frontPage,
        };
    };

    const uploadImage = (file, setProgress, setUploading, callback) => {
        handleUploadFileSignedUrl({
            file,
            size: 1024 * 1024 * 100, // 100MB
            onInit: (size) => {
                setProgress(0);
                setUploading(true);
            },
            onProgress: (loaded, total) => {
                setProgress(Math.min(100, Math.round((loaded / total) * 100)));
            },
            onComplete: (url) => {
                setUploading(false);
                callback(url);
            },
            onError: (error) => {
                setUploading(false);
                if (error.code !== 'ERR_CANCELED') {
                    enqueueSnackbar(t('app.common.uploadError'), {
                        variant: 'error',
                    });
                }
            },
            bucketType: 'resources',
            signal: getSignal(),
        });
    };

    const updateAccount = async () => {
        setValues({
            ...values,
            isUpdating: true,
            submited: false,
        });

        const userDto = getDto();
        dispatch(
            setUserAction({
                user: userDto,
                onSuccess: () => {
                    enqueueSnackbar(t('profile.organization.saveSuccess'), { variant: 'success' });
                    setValues({
                        ...values,
                        isUpdating: false,
                        submited: true,
                    });

                    dispatch(setMyAccountSettingsDirty(false));
                },
                onFailure: () => {
                    enqueueSnackbar(t('app.common.error'), { variant: 'error' });
                    setValues({
                        ...values,
                        isUpdating: false,
                        submited: true,
                    });

                    dispatch(setMyAccountSettingsDirty(false));
                },
            }),
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        updateAccount();
    };

    const aboutChange = (value) => {
        dispatch(setMyAccountSettingsDirty(true));
        setAbout(value);
    };

    const descriptionError = false;

    const onUploading = (uploading) => {
        setUploadingProfileImage(uploading);
    };
    useEffect(() => {
        ValidatorForm.addValidationRule('whiteSpaces', (value) => {
            const regex = /^\s+$/g;
            return !value?.match(regex);
        });
        return () => {
            ValidatorForm.removeValidationRule('whiteSpaces');
        };
    }, []);

    useEffect(() => {
        ValidatorForm.addValidationRule('validateName', (value) => {
            const regExName = /^[a-zA-Z0-9ñáéíóú ]+$/;
            return Boolean(value.match(regExName));
        });
        return () => {
            ValidatorForm.removeValidationRule('validateName');
        };
    }, []);

    function handleError() {
        enqueueSnackbar(t('product.productFormVariants.formError'), {
            variant: 'error',
        });
    }

    // Load integration data
    const loadData = useCallback(() => {
        if (integrationProviderType === '') {
            dispatch(fetchCalendarIntegrationAction(organizationId));
        }
    }, [dispatch, organizationId, integrationProviderType]);

    useEffect(() => {
        if (calendarVersion !== 3) return;

        loadData();
    }, [loadData, calendarVersion]);

    // Check if the calendar integration is connected and if the user has the required credentials
    const loadHasIntegrationCredentialData = useCallback(() => {
        if (calendarVersion === 3 && isCalendarConnected && integrationCredentialLevel === 'Calendar') {
            dispatch(fetchHasCredentialAction({ organizationId, calendarId: user.userId }));
        }
    }, [dispatch, organizationId, user.userId, isCalendarConnected, integrationCredentialLevel, calendarVersion]);

    useEffect(() => {
        loadHasIntegrationCredentialData();
    }, [loadHasIntegrationCredentialData]);

    const loadHasSubscription = useCallback(() => {
        if (calendarVersion !== 3 || !hasCredential) return;

        const baseUrl = `integrations/has-subscription?calendarId=${user.userId}`;
        api.get(baseUrl)
            .then((response) => {
                if (response.data?.hasSubscription) {
                    setHasSubscription(true);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, [user.userId, hasCredential, calendarVersion]);

    useEffect(() => {
        loadHasSubscription();
    }, [loadHasSubscription]);

    const handleAuthCalendarIntegration = useCallback(
        (provider) => {
            if (!hasCredential) {
                const baseUrl = `integrations/${provider}/auth-url`;
                const authUrl = `${baseUrl}?organizationId=${organizationId}&calendarId=${user.userId}`;
                api.get(authUrl)
                    .then((response) => {
                        window.location.href = response.data.authUrl;
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            } else {
                // Remove calendar credentials
                const baseUrl = `integrations/${user.userId}/remove-credential`;
                api.post(baseUrl)
                    .then((response) => {
                        if (response.data?.success) {
                            dispatch(setHasCredential({ hasCredential: false }));
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            }
        },
        [dispatch, organizationId, user.userId, hasCredential],
    );

    const integrationActionsVisible = useCallback(() => {
        return calendarVersion === 3 && isCalendarConnected && integrationCredentialLevel === 'Calendar';
    }, [calendarVersion, isCalendarConnected, integrationCredentialLevel]);

    const isAuthVisible = useCallback(() => {
        return !hasCredential && !pendingHasCredential;
    }, [hasCredential, pendingHasCredential]);

    const handleSuscription = useCallback(() => {
        const baseUrl = `integrations/${user.userId}/${hasSubscription ? 'unsubscribe' : 'subscribe'}`;
        api.post(baseUrl)
            .then((response) => {
                if (response.data?.success) {
                    setHasSubscription(hasSubscription ? false : true);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, [hasSubscription, user.userId]);

    const handleRefreshSubscription = useCallback(() => {
        const baseUrl = `integrations/${user.userId}/refresh-subscription`;
        api.post(baseUrl)
            .then((response) => {
                if (response.data?.success) {
                    enqueueSnackbar(t('profile.settings.iCalendar.icalendarRefreshSubscriptionAction'), {
                        variant: 'success',
                    });
                }
            })
            .catch((error) => {
                console.error(error);
                enqueueSnackbar(t('app.common.error'), { variant: 'error' });
            });
    }, [enqueueSnackbar, t, user.userId]);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const authBody = useCallback(() => {
        if (isCalendarConnected && integrationCredentialLevel === 'Calendar') {
            if (integrationProviderType !== 'Multiple') {
                return (
                    <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                        <Box sx={{ display: 'inline-flex' }}>
                            <Button
                                variant="contained"
                                className={classes.redColor}
                                size="small"
                                onClick={() => handleAuthCalendarIntegration(integrationProviderType.toLowerCase())}
                            >
                                <div style={{ display: 'inline-flex', justifyContent: 'center', marginRight: '5px' }}>
                                    {integrationProviderType === 'Google' ? (
                                        <Google fontSize="small" />
                                    ) : (
                                        <SvgIcon color="gray">
                                            <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z" />
                                        </SvgIcon>
                                    )}
                                </div>
                                {isAuthVisible()
                                    ? t('profile.settings.iCalendar.icalendarAuthAction')
                                    : t('profile.settings.iCalendar.icalendarRevokeAuthAction')}
                            </Button>
                        </Box>
                    </Box>
                );
            } else {
                return (
                    <>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ width: '100%' }}
                        >
                            <Box sx={{ display: 'inline-flex' }}>
                                <Button
                                    variant="contained"
                                    className={classes.redColor}
                                    size="small"
                                    onClick={(event) => {
                                        if (isAuthVisible()) {
                                            setAnchorEl(event.currentTarget);
                                        } else {
                                            handleAuthCalendarIntegration();
                                        }
                                    }}
                                >
                                    <div
                                        style={{ display: 'inline-flex', justifyContent: 'center', marginRight: '5px' }}
                                    >
                                        <SvgIcon viewBox="0 0 48 24">
                                            <path
                                                d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972
                                                    c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032
                                                    c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814
                                                    C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12
                                                    s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
                                            />

                                            <path
                                                transform="translate(24, 0)"
                                                d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35
                                                -.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36
                                                -.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32
                                                -.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3
                                                -.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85
                                                l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83
                                                l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73z
                                                M9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.2
                                                8.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.6
                                                4.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.1
                                                7.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24
                                                l-5.9 3.54Z"
                                            />
                                        </SvgIcon>
                                    </div>
                                    {isAuthVisible()
                                        ? t('profile.settings.iCalendar.icalendarAuthAction')
                                        : t('profile.settings.iCalendar.icalendarRevokeAuthAction')}
                                </Button>
                            </Box>
                        </Box>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            slotProps={{
                                paper: {
                                    className: cx(
                                        flexBox.overflowHidden,
                                        flexBox.relative,
                                        flexBox.borderRadius2,
                                        flexBox.marginTop05,
                                    ),
                                    style: {
                                        width: anchorEl ? `${anchorEl.clientWidth}px` : 'auto',
                                        boxShadow:
                                            'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px',
                                    },
                                },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem key={'google'} onClick={() => handleAuthCalendarIntegration('google')}>
                                <ListItemIcon>
                                    <Google fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>{t('profile.settings.iCalendar.icalendarAuthAction')}</ListItemText>
                            </MenuItem>
                            <MenuItem key={'microsoft'} onClick={() => handleAuthCalendarIntegration('microsoft')}>
                                <ListItemIcon>
                                    <SvgIcon color="gray">
                                        <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z" />
                                    </SvgIcon>
                                </ListItemIcon>
                                <ListItemText>{t('profile.settings.iCalendar.icalendarAuthAction')}</ListItemText>
                            </MenuItem>
                        </Menu>
                    </>
                );
            }
        } else {
            return <></>;
        }
    }, [
        isCalendarConnected,
        integrationCredentialLevel,
        integrationProviderType,
        anchorEl,
        handleAuthCalendarIntegration,
        handleClose,
        t,
        isAuthVisible,
        classes.redColor,
        cx,
        flexBox.overflowHidden,
        flexBox.relative,
        flexBox.borderRadius2,
        flexBox.marginTop05,
    ]);

    return (
        <div className={classes.root}>
            <div className={cx(classes.container, classes.scroll)}>
                <div className={cx(classes.body, classes.mW80)}>
                    <ValidatorForm onSubmit={handleSubmit} onError={handleError}>
                        <ProfileImage
                            image={values.avatar}
                            setImage={setAvatar}
                            submitted={values.submitted}
                            uploadImage={uploadImage}
                            onUploading={onUploading}
                            required={false}
                        />

                        <FormGroup className={classes.formGroup}>
                            <FormLabel>{t('profile.myAccount.name')}</FormLabel>
                            <CustomTextValidator
                                name="firstName"
                                value={values.firstName}
                                variant="outlined"
                                onChange={(e) => {
                                    dispatch(setMyAccountSettingsDirty(true));
                                    setValues({ ...values, firstName: e.target.value });
                                }}
                                fullWidth={true}
                                size="small"
                                validators={['required', 'whiteSpaces', 'validateName']}
                                errorMessages={[
                                    t('license.licenseForm.emailRequired'),
                                    t('license.licenseForm.emailRequired'),
                                    t('myAccount.formValidator.textFormat'),
                                ]}
                            />
                        </FormGroup>

                        <FormGroup className={classes.formGroup}>
                            <FormLabel>{t('profile.myAccount.lastName')}</FormLabel>
                            <CustomTextValidator
                                name="lastName"
                                value={values.lastName}
                                onChange={(e) => {
                                    dispatch(setMyAccountSettingsDirty(true));
                                    setValues({ ...values, lastName: e.target.value });
                                }}
                                variant="outlined"
                                fullWidth={true}
                                size="small"
                                validators={['whiteSpaces', 'validateName']}
                                errorMessages={[
                                    t('license.licenseForm.emailRequired'),
                                    t('myAccount.formValidator.textFormat'),
                                ]}
                            />
                        </FormGroup>

                        <FormGroup className={classes.formGroup}>
                            <FormLabel>{t('profile.myAccount.username')}</FormLabel>
                            <CustomTextValidator
                                name="username"
                                value={values.username}
                                onChange={(e) => {
                                    dispatch(setMyAccountSettingsDirty(true));
                                    setValues({ ...values, username: e.target.value });
                                }}
                                variant="outlined"
                                fullWidth={true}
                                size="small"
                                validators={['required', 'whiteSpaces']}
                                errorMessages={[
                                    t('license.licenseForm.emailRequired'),
                                    t('license.licenseForm.emailRequired'),
                                ]}
                            />
                        </FormGroup>

                        {integrationActionsVisible() && (
                            <div className={classes.pt20px}>
                                <Typography className={classes.accountSectionTitle}>
                                    {t('profile.settings.iCalendar')}
                                </Typography>
                                {authBody()}
                                {!isAuthVisible() && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        style={{ width: '100%' }}
                                        className={classes.pt10px}
                                    >
                                        <Box sx={{ display: 'inline-flex' }}>
                                            <Button
                                                variant="contained"
                                                className={classes.redColor}
                                                size="small"
                                                onClick={handleSuscription}
                                            >
                                                {hasSubscription
                                                    ? t('profile.settings.iCalendar.icalendarUnsubscribeAction')
                                                    : t('profile.settings.iCalendar.icalendarSubscribeAction')}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                                {!isAuthVisible() && hasSubscription && (
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        style={{ width: '100%' }}
                                        className={classes.pt10px}
                                    >
                                        <Box sx={{ display: 'inline-flex' }}>
                                            <Button
                                                variant="contained"
                                                className={classes.redColor}
                                                size="small"
                                                onClick={handleRefreshSubscription}
                                            >
                                                {t('profile.settings.iCalendar.icalendarRefreshSubscriptionAction')}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </div>
                        )}

                        <FormGroup className={classes.formGroup}>
                            <FormLabel>{t('profile.myAccount.about')}</FormLabel>
                            <FormControl
                                className={cx(classes.formControl, descriptionError && classes.descriptionError)}
                            >
                                <CustomSunEditor
                                    readOnly={readOnly}
                                    defaultValue={about}
                                    setOptions={editorConfig}
                                    sunError={setSunError}
                                    onChange={aboutChange}
                                />
                            </FormControl>
                        </FormGroup>

                        <FormGroup className={classes.formGroup}>
                            <FormLabel>{t('profile.myAccount.headline')}</FormLabel>
                            <FormControl className={classes.formControl}>
                                <TextField
                                    name="headline"
                                    value={values.headline}
                                    className={classes.textField}
                                    onChange={(e) => {
                                        dispatch(setMyAccountSettingsDirty(true));
                                        setValues({ ...values, headline: e.target.value });
                                    }}
                                    variant="outlined"
                                    size="small"
                                    rows={3}
                                    multiline={true}
                                />
                            </FormControl>
                        </FormGroup>

                        <FrontImage
                            image={values.frontPage}
                            setImage={setFrontPage}
                            submitted={values.submitted}
                            uploadImage={uploadImage}
                            required={false}
                            label={t('profile.myAccount.frontPage')}
                        />

                        {newCalendar && isAuthorize(['oneToOne.iCall']) && !isAuthorize(['oneToOne.iCalendar']) && (
                            <FormGroup className={classes.formGroup}>
                                <FormLabel>{t('common.term.Calendar')}</FormLabel>
                                <OCalendar isReadOnly={false} />
                            </FormGroup>
                        )}

                        {isFormDirty && (
                            <ActionButton
                                type="submit"
                                disableElevation={true}
                                spinner={uploadingProfileImage}
                                disabled={uploadingProfileImage || sunError}
                                className={cx(classes.proceedButton, classes.buttonSave)}
                            >
                                <span>{t('profile.myAccount.save')}</span>
                            </ActionButton>
                        )}
                    </ValidatorForm>
                </div>
            </div>
        </div>
    );
};

export default MyAccount;
